import styles from './Slider.module.scss';

import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import cn from 'classnames';

import { swipeBehavior } from '@telegram-apps/sdk-react';
import React, { useEffect, useMemo } from 'react';
import { Range } from 'react-range';

import { Thumb } from './Thumb';

type SliderProps = {
  isLoading?: boolean;
  value: number;
  onChange?: (value: number) => void;
  onDragEnd?: () => void;
  maxValue?: number;
  minValue?: number;
  colors?: [string, string, string];
  step?: number;
  trackHeight?: number;
  disabled?: boolean;
  children?: React.ReactNode;
};

export const Slider: React.FC<SliderProps> = ({
  isLoading = false,
  onDragEnd,
  value,
  onChange,
  colors = ['#7076ff', '#d04dac', '#ff6a49'],
  maxValue = 100,
  minValue = 0,
  step = 1,
  trackHeight = 13,
  disabled = false,
  children,
}) => {
  const { triggerHapticFeedback } = useHapticFeedback();
  const percents = useMemo(() => {
    return (value / (maxValue - minValue)) * 100;
  }, [value, minValue, maxValue]);

  const handleOnChange = (values: number[]) => {
    triggerHapticFeedback('medium');
    if (swipeBehavior.disableVertical.isAvailable()) {
      swipeBehavior.disableVertical();
    }
    if (onChange) onChange(values[0]);
  };

  useEffect(() => {
    if (swipeBehavior.mount.isAvailable()) {
      swipeBehavior.mount();
    }
  }, []);

  const handleOnFinalChange = () => {
    triggerHapticFeedback('medium');

    if (swipeBehavior.enableVertical.isAvailable()) {
      swipeBehavior.enableVertical();
    }

    if (onDragEnd) onDragEnd();
  };

  if (isLoading) {
    return ''; // todo loading state here
  }

  return (
    <div
      className={styles.slider}
      style={
        {
          height: `${trackHeight}px`,
          '--color1': colors[0],
          '--color2': colors[1],
          '--color3': colors[2],
        } as React.CSSProperties
      }
    >
      <div className={cn(styles.sliderContainer, 'swiper-no-swiping')}>
        <Range
          step={step}
          min={minValue}
          max={maxValue}
          values={[value]}
          disabled={disabled}
          onChange={handleOnChange}
          onFinalChange={handleOnFinalChange}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              style={{ '--val': percents } as React.CSSProperties}
              className={styles.track}
            >
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div {...props} key={props.key} className={styles.thumb}>
              <Thumb colors={colors} />
              {children}
            </div>
          )}
        />
      </div>
    </div>
  );
};
